import React, { FunctionComponent } from 'react';
import { useForm, Controller } from 'react-hook-form';
import moment, { Moment } from 'moment';
import styled from 'styled-components';

import { SubmitButton } from 'components';
import { FormGroup, Heading, Message, DatePicker } from 'components/chroma';
import { TextSmall } from 'components/chroma/extended';

import { HomeFullPageProps } from 'scenes/home/types';
import { getMissingPolicyStartDate } from 'scenes/home/selectors';
import analytics from 'scenes/home/analytics';

const MessageWrapper = styled.div`
  margin-top: -0.5rem;
`;

const POLICY_START_DATE = 'policyStartDate';
const MAXIMUM_FUTURE_DAYS = 60;

const isOutsideRange = (date: Moment) =>
  date.isBefore(moment().format('YYYY-MM-DD')) ||
  date.isAfter(moment().add(MAXIMUM_FUTURE_DAYS, 'day').format('YYYY-MM-DD')) ||
  date.isAfter(moment('2024-09-01').format('YYYY-MM-DD'));

const ReviewPolicyStartDateStep: FunctionComponent<HomeFullPageProps> = ({
  funnelState,
  onUpdatePolicyStartDate,
}) => {
  const {
    formState: hooksForm,
    handleSubmit,
    control,
    getValues,
    errors,
  } = useForm({
    mode: 'onChange',
  });

  const onSubmit = () => {
    analytics().retrieveQuoteComplete(funnelState);
    onUpdatePolicyStartDate(getValues(POLICY_START_DATE));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Heading size={2}>Retrieve Quote</Heading>
      <Controller
        control={control}
        name={POLICY_START_DATE}
        defaultValue={getMissingPolicyStartDate(funnelState)}
        rules={{
          validate: {
            validDate: (value) =>
              (value && !isOutsideRange(moment(value))) || 'Invalid date',
          },
        }}
        render={({ onChange, value }) => (
          <FormGroup
            id={POLICY_START_DATE}
            label="Please select policy start date."
            helpText="This will not affect your premium."
            error={errors[POLICY_START_DATE]?.message || ''}
          >
            <DatePicker
              id={POLICY_START_DATE}
              screenReaderInputMessage="Enter policy start date"
              displayFormat="DD/MM/YYYY"
              isOutsideRange={isOutsideRange}
              numberOfMonths={1}
              value={value}
              onChange={onChange}
            />
          </FormGroup>
        )}
      />

      <MessageWrapper>
        <Message info margin={{ bottom: 5 }}>
          <TextSmall>
            This policy can start any date before 1 September 2024.
          </TextSmall>
        </Message>
      </MessageWrapper>

      <SubmitButton enabled={hooksForm.isValid}>Next</SubmitButton>
    </form>
  );
};

export default ReviewPolicyStartDateStep;
